import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Trophy, Calendar, MapPin, Users, Award, ChevronRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}
function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export default function TournamentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/tournaments/${id}`);
      setT(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) {
    return (
      <div className="p-10 text-center">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-red-400">{error}</div>
        <Link to="/tournaments" className="btn-secondary mt-6" data-testid="back-to-tournaments">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </Link>
      </div>
    );
  }
  if (!t) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  const registered = t.participants?.includes(user?.user_id);

  const toggle = async () => {
    setBusy(true);
    try {
      if (registered) await api.post(`/tournaments/${id}/unregister`);
      else await api.post(`/tournaments/${id}/register`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 fade-in" data-testid="tournament-detail-page">
      <Link to="/tournaments" className="text-white/50 text-xs font-mono uppercase tracking-[0.2em] link-underline hover:text-primary" data-testid="back-link">
        ← Wettkämpfe
      </Link>

      <div className="card-solid overflow-hidden tracing-border">
        {t.image_url && (
          <div className="relative h-56 md:h-72">
            <img src={t.image_url} alt={t.name} className="w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/70 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="chip bg-black/40 backdrop-blur-sm text-primary border-primary/40">
                {t.status === "live" && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                {t.status}
              </div>
              <h1 className="font-display text-3xl md:text-5xl tracking-tight mt-3">{t.name}</h1>
            </div>
          </div>
        )}

        <div className="p-6 md:p-8 space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { icon: Trophy, label: "Game", value: t.game },
              { icon: Calendar, label: "Start", value: `${formatDate(t.start_time)} · ${formatTime(t.start_time)}` },
              { icon: MapPin, label: "Location", value: t.location },
              { icon: Award, label: "Prize", value: t.prize || "—" },
            ].map((d) => (
              <div key={d.label} className="bg-bg p-4">
                <div className="flex items-center gap-2 text-white/40">
                  <d.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{d.label}</span>
                </div>
                <div className="font-mono tabular-nums text-sm mt-2 truncate">{d.value}</div>
              </div>
            ))}
          </div>

          {t.description && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Beschreibung</div>
              <p className="text-white/80 leading-relaxed">{t.description}</p>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Users className="w-4 h-4" />
              <span className="font-mono tabular-nums">
                {t.participants?.length || 0}/{t.max_players}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Teilnehmer</span>
            </div>
            {t.status !== "completed" && (
              <button
                onClick={toggle}
                disabled={busy}
                className={registered ? "btn-secondary" : "btn-primary"}
                data-testid="tournament-register-btn"
              >
                {busy ? "…" : registered ? "Abmelden" : "Anmelden"}
              </button>
            )}
          </div>

          {t.participants_detail?.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Teilnehmer</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {t.participants_detail.map((p) => (
                  <div key={p.user_id} className="flex items-center gap-3 p-3 border border-white/5 hover:border-primary/30 transition-colors" data-testid={`participant-${p.user_id}`}>
                    {p.picture ? (
                      <img src={p.picture} alt="" className="w-8 h-8 rounded-sm object-cover border border-white/10" />
                    ) : (
                      <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/30 text-primary flex items-center justify-center font-mono text-xs">
                        {p.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm truncate">{p.name}</span>
                    <ChevronRight className="w-3 h-3 ml-auto text-white/30" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
